import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  seasonalPoojaService, 
  FamilyMember,
  CalculatePricingRequest,
  CalculatePricingResponse
} from '../../services/seasonalPoojaService';
import './SeasonalPoojaBooking.css';
import SEOHead from '../SEOHead';
import YouTubeSection, { YouTubeVideo } from '../YouTubeSection';
import OptimizedImage from '../OptimizedImage';
import { trackEventWithUTM, trackBeginCheckout } from '../../utils/analytics';

// ── Countdown helpers ───────────────────────────────────────────────────────
const NAVRATRI_DATE = new Date('2026-03-26T08:00:00');
const TOTAL_SLOTS = 108;
const BOOKED_SLOTS = 108; // will be made dynamic later

// ── YouTube videos ──────────────────────────────────────────────────────────
const seasonalPoojaVideos: YouTubeVideo[] = [
  {
    videoId: '6128VDrIiNQ',
    caption: 'Shanti Path Pujan – Performed at Home for Peace & Harmony',
  },
  {
    videoId: 'kXm7GHrOB2k',
    caption: 'Customer Speaks About Their Puja Experience – Real Stories from Real Families',
  },
  {
    videoId: '3TxLb11r4yI',
    caption: 'What our Acharyas Say – About Antaryatra & the Importance of Vedic Rituals',
  },
];

function useCountdown(target: Date) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  });
  return time;
}

// ── Component ─────────────────────────────────────────────────────────────────
// ── Component ─────────────────────────────────────────────────────────────────────────────────
type Plan = 'india' | 'international';

const SeasonalPoojaBooking: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [showMissedPopup, setShowMissedPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan>('india');
  const formRef = useRef<HTMLDivElement>(null);
  const plansRef = useRef<HTMLDivElement>(null);
  // Funnel tracking refs — each fires only once so we don't double-count
  const plansViewedTracked = useRef(false);
  const primaryDetailsTracked = useRef(false);
  const [error, setError] = useState<string>('');
  
  // Primary member details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gotra, setGotra] = useState('');
  const [place, setPlace] = useState('');
  const [occasionName, setOccasionName] = useState('Navratri Puja');
  const [preferredDate, setPreferredDate] = useState('2026-03-26');
  const [specialRequirements, setSpecialRequirements] = useState('');
  
  // Additional family members
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  
  // Pricing
  const [pricingData, setPricingData] = useState<CalculatePricingResponse | null>(null);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countdown = useCountdown(NAVRATRI_DATE);
  const slotsLeft = TOTAL_SLOTS - BOOKED_SLOTS;

  const handleOpenForm = (plan: Plan = 'india') => {
    setSelectedPlan(plan);
    setShowForm(true);
    // GA4 funnel step 2: user chose a plan and opened the booking form
    trackEventWithUTM('spb_plan_selected', 'seasonal_pooja_funnel', plan);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // Called by the Back button — records how far the user got before abandoning
  const handleBackFromForm = () => {
    trackEventWithUTM('spb_form_abandoned', 'seasonal_pooja_funnel', selectedPlan, {
      had_primary_details: primaryDetailsTracked.current,
      family_member_count: familyMembers.length,
      had_pricing: !!pricingData,
      amount_seen: pricingData?.finalAmount ?? 0,
    });
    setShowForm(false);
  };

  const handleScrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Comprehensive form validation functions
  const validateFirstName = (name: string): { isValid: boolean; error?: string } => {
    const trimmed = name.trim();
    if (!trimmed) return { isValid: false, error: 'First name is required' };
    if (trimmed.length > 50) return { isValid: false, error: 'First name must be 50 characters or less' };
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return { isValid: false, error: 'First name should contain only letters' };
    return { isValid: true };
  };

  const validateLastName = (name: string): { isValid: boolean; error?: string } => {
    const trimmed = name.trim();
    if (!trimmed) return { isValid: false, error: 'Last name is required' };
    if (trimmed.length < 2) return { isValid: false, error: 'Last name must be at least 2 characters' };
    if (trimmed.length > 50) return { isValid: false, error: 'Last name must be 50 characters or less' };
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return { isValid: false, error: 'Last name should contain only letters' };
    return { isValid: true };
  };

  const validateGotra = (gotra: string): { isValid: boolean; error?: string } => {
    const trimmed = gotra.trim();
    if (trimmed && trimmed.length > 40) return { isValid: false, error: 'Gotra must be 40 characters or less' };
    if (trimmed && !/^[a-zA-Z\s]+$/.test(trimmed)) return { isValid: false, error: 'Gotra should contain only letters' };
    return { isValid: true };
  };

  // Helper function to validate if lastName meets minimum requirements
  const isValidLastName = (lastName: string): boolean => {
    return validateLastName(lastName).isValid;
  };

  // Family member validation
  const validateFamilyMember = (member: FamilyMember): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    const firstNameCheck = validateFirstName(member.firstName);
    if (!firstNameCheck.isValid) errors.push(`First Name: ${firstNameCheck.error}`);
    
    const lastNameCheck = validateLastName(member.lastName);
    if (!lastNameCheck.isValid) errors.push(`Last Name: ${lastNameCheck.error}`);
    
    if (member.gotra) {
      const gotraCheck = validateGotra(member.gotra);
      if (!gotraCheck.isValid) errors.push(`Gotra: ${gotraCheck.error}`);
    }
    
    if (member.age <= 0 || member.age > 120) {
      errors.push('Age must be between 1 and 120');
    }
    
    return { isValid: errors.length === 0, errors };
  };

  // Validate only family member data — primary member is NOT required for pricing
  const hasFamilyMemberData = useCallback((): boolean => {
    for (const member of familyMembers) {
      if (member.firstName.trim() || member.lastName.trim()) {
        if (!member.firstName.trim() || !member.lastName.trim()) return false;
        if (!validateFirstName(member.firstName).isValid || !validateLastName(member.lastName).isValid) return false;
        if (member.gotra && !validateGotra(member.gotra).isValid) return false;
      }
    }
    return true;
  }, [familyMembers]);

  const hasPrimaryMember = useCallback((): boolean => {
    return validateFirstName(firstName).isValid &&
           validateLastName(lastName).isValid &&
           (!gotra || validateGotra(gotra).isValid);
  }, [firstName, lastName, gotra]);

const isValidPricingData = (data: CalculatePricingResponse | null): data is CalculatePricingResponse => {
    return !!(
      data &&
      typeof data.basePrice === 'number' &&
      typeof data.calculatedMembersCount === 'number' &&
      typeof data.totalAmount === 'number' &&
      typeof data.gstRate === 'number' &&
      typeof data.gstAmount === 'number' &&
      typeof data.finalAmount === 'number'
    );
  };

  // Scroll to top when component mounts
  useEffect(() => {
    // Re-open form if returning from payment page
    const navState = location.state as { openForm?: boolean; selectedPlan?: Plan } | null;
    if (navState?.openForm) {
      setSelectedPlan(navState.selectedPlan || 'india');
      setShowForm(true);
      // Clear the navigation state so a refresh doesn't re-open the form
      window.history.replaceState({}, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // page_view is handled automatically by usePageTracking in App.tsx
  }, []);

  const calculateTotalPrice = useCallback(async () => {
    try {
      setIsCalculatingPrice(true);

      const isGlobal = selectedPlan === 'international';
      const completedFamilyMembers = familyMembers
        .filter(m => m.firstName.trim() && m.lastName.trim())
        .map(m => ({
          firstName: m.firstName.trim(),
          lastName: m.lastName.trim(),
          relationship: m.relationship,
          age: m.age,
          gotra: m.gotra?.trim() || undefined
        }));

      // Build minimal or full payload depending on primary member data
      const request: CalculatePricingRequest = hasPrimaryMember()
        ? {
            global: isGlobal,
            primaryMember: {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              gotra: gotra.trim() || undefined
            },
            additionalFamilyMembers: completedFamilyMembers
          }
        : { global: isGlobal };

      const priceResponse = await seasonalPoojaService.calculatePricing(request);
      console.log('📊 Pricing API Response:', priceResponse);

      if (!isValidPricingData(priceResponse)) {
        console.error('❌ Invalid pricing data received:', priceResponse);
        throw new Error('Invalid pricing data received from API');
      }

      setPricingData(priceResponse);
      setTotalPrice(priceResponse.finalAmount);
      setError('');
      // GA4 funnel step 4: user saw the calculated price
      trackEventWithUTM('spb_pricing_calculated', 'seasonal_pooja_funnel', selectedPlan, {
        amount: priceResponse.finalAmount,
        member_count: priceResponse.calculatedMembersCount,
      });
    } catch (err) {
      console.error('Error calculating price:', err);
      setPricingData(null);
      setTotalPrice(0);
      setError('Failed to calculate pricing. Please check your details and try again.');
    } finally {
      setIsCalculatingPrice(false);
    }
  }, [firstName, lastName, gotra, familyMembers, selectedPlan, hasPrimaryMember]);

  // GA4 funnel step 1: fire once when the plans section scrolls into view (landing page)
  useEffect(() => {
    if (showForm || !plansRef.current || plansViewedTracked.current) return;
    const node = plansRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !plansViewedTracked.current) {
          plansViewedTracked.current = true;
          trackEventWithUTM('spb_plans_viewed', 'seasonal_pooja_funnel', 'navratri_2026');
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [showForm]);

  // GA4 funnel step 3: fire once when primary member name becomes valid
  useEffect(() => {
    if (!showForm || primaryDetailsTracked.current) return;
    if (hasPrimaryMember()) {
      primaryDetailsTracked.current = true;
      trackEventWithUTM('spb_primary_details_entered', 'seasonal_pooja_funnel', selectedPlan);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, showForm, selectedPlan]);

  // 1. Fire immediately when form opens or plan toggle changes
  useEffect(() => {
    if (showForm) {
      calculateTotalPrice();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, selectedPlan]);

  // 2. Debounced recalculation when member details change (500 ms)
  useEffect(() => {
    if (!showForm) return;
    const timeoutId = setTimeout(() => {
      if (hasFamilyMemberData()) {
        calculateTotalPrice();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [firstName, lastName, gotra, familyMembers, showForm, hasFamilyMemberData, calculateTotalPrice]);

  const handleAddFamilyMember = () => {
    const maxFamilyMembers = 10; // Fixed limit as mentioned in requirements
    
    if (familyMembers.length >= maxFamilyMembers) {
      alert(`Maximum ${maxFamilyMembers} family members allowed`);
      return;
    }
    
    const updated = [...familyMembers, { firstName: '', lastName: '', relationship: '', age: 0, gotra: '' }];
    setFamilyMembers(updated);
    trackEventWithUTM('spb_family_member_added', 'seasonal_pooja_funnel', selectedPlan, {
      total_members: updated.length,
    });
  };

  const handleRemoveFamilyMember = (index: number) => {
    const updatedMembers = familyMembers.filter((_, i) => i !== index);
    setFamilyMembers(updatedMembers);
    trackEventWithUTM('spb_family_member_removed', 'seasonal_pooja_funnel', selectedPlan, {
      remaining_members: updatedMembers.length,
    });
  };

  const handleFamilyMemberChange = (
    index: number,
    field: keyof FamilyMember,
    value: string
  ) => {
    const updatedMembers = [...familyMembers];
    if (field === 'age') {
      // Convert string to number for age field
      updatedMembers[index] = { ...updatedMembers[index], [field]: parseInt(value) || 0 };
    } else {
      updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    }
    setFamilyMembers(updatedMembers);
  };

  const validateForm = (): boolean => {
    const fail = (reason: string, message: string) => {
      trackEventWithUTM('spb_form_validation_failed', 'seasonal_pooja_funnel', reason, { plan: selectedPlan });
      alert(message);
    };

    if (!firstName.trim()) {
      fail('missing_first_name', 'Please enter first name');
      return false;
    }
    if (!lastName.trim()) {
      fail('missing_last_name', 'Please enter last name');
      return false;
    }
    if (!occasionName.trim()) {
      fail('missing_occasion', 'Please enter occasion name');
      return false;
    }
    if (!preferredDate) {
      fail('missing_date', 'Please select preferred date');
      return false;
    }
    if (!isValidPricingData(pricingData)) {
      fail('pricing_not_ready', 'Please wait for pricing calculation to complete');
      return false;
    }
    for (let i = 0; i < familyMembers.length; i++) {
      const member = familyMembers[i];
      if (!member.firstName.trim()) {
        fail('family_member_first_name', `Please enter first name for family member ${i + 1}`);
        return false;
      }
      if (!member.lastName.trim()) {
        fail('family_member_last_name', `Please enter last name for family member ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleProceedToPayment = async () => {
    console.log('🚀 Proceed to Payment clicked');
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    console.log('✅ Form validation passed');
    // GA4 funnel step 5: validation passed — user is genuinely proceeding to payment
    trackBeginCheckout(
      [{
        item_id: 'seasonal_pooja_navratri_2026',
        item_name: 'Maha Ashtami Navratri Puja',
        item_category: 'Seasonal Pooja',
        item_variant: selectedPlan === 'international' ? 'International' : 'India',
        price: pricingData?.finalAmount ?? 0,
        quantity: pricingData?.calculatedMembersCount ?? 1,
      }],
      pricingData?.finalAmount ?? 0
    );
    trackEventWithUTM('spb_proceed_to_payment', 'seasonal_pooja_funnel', selectedPlan, {
      amount: pricingData?.finalAmount ?? 0,
      member_count: pricingData?.calculatedMembersCount ?? 1,
    });
    
    try {
      setIsSubmitting(true);
      console.log('🏗️ Creating booking...');
      
      // Create booking
      const bookingResponse = await seasonalPoojaService.createBooking({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        gotra: gotra.trim() || undefined,
        occasionName: occasionName.trim(),
        preferredDate: preferredDate,
        specialRequirements: specialRequirements.trim() || undefined,
        global: selectedPlan === 'international',
        additionalFamilyMembers: familyMembers.map(m => ({
          firstName: m.firstName.trim(),
          lastName: m.lastName.trim(),
          relationship: m.relationship.trim(),
          age: m.age,
          gotra: m.gotra?.trim() || undefined
        }))
      });
      
      console.log('✅ Booking created:', bookingResponse);
      
      // Navigate to payment page with booking details
      console.log('🧭 Navigating to payment page...');
      navigate('/seasonal-pooja/payment', {
        state: {
          bookingUuid: bookingResponse.bookingUuid,
          occasionName: bookingResponse.occasionName,
          preferredDate: bookingResponse.preferredDate,
          primaryMember: {
            firstName: bookingResponse.firstName,
            lastName: bookingResponse.lastName,
            gotra: bookingResponse.gotra
          },
          familyMembers: bookingResponse.familyMembers,
          finalAmount: bookingResponse.finalAmount,
          pricingData: pricingData,
          razorpayKeyId: bookingResponse.razorpayKeyId,
          selectedPlan: selectedPlan
        }
      });
    } catch (err) {
      console.error('❌ Error creating booking:', err);
      trackEventWithUTM('spb_booking_api_error', 'seasonal_pooja_funnel', selectedPlan, {
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      alert('Failed to create booking. Please try again.');
    } finally {
      console.log('🏁 Setting isSubmitting to false');
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="seasonal-pooja-error">
        <div className="error-icon">⚠️</div>
        <h2>{error}</h2>
        <button onClick={() => setError('')} className="btn-back-home">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="seasonal-pooja-booking">
      <SEOHead
        title="Chaitra Navratri Maha Ashtami Durga Puja Booking"
        description="Participate in Maha Ashtami Durga Puja from anywhere in the world. Receive blessings and prashadam delivered to your home."
        canonicalPath="/seasonal-pooja"
        keywords="Maha Ashtami puja, Navratri puja booking, Durga puja online, prashadam delivery"
      />

      {/* ── LANDING PAGE ── */}
      {!showForm && (
        <div className="spb-landing">

          {/* 0. ANNOUNCEMENT STRIP */}
          <div className="spb-announce-strip">
            <div className="spb-announce-content">
              <div className="spb-announce-text">
                <span className="spb-announce-title">🪔 Navratri Special: Maha Ashtami Puja</span>
                {/* <span className="spb-announce-sub">Limited Sankalp Slots Available&nbsp;&nbsp;|&nbsp;&nbsp;Worldwide Participation</span> */}
                <span className="spb-announce-sub">Yo just missed it, All Slots Booked &nbsp;&nbsp;|&nbsp;&nbsp;Thanks for your Participation</span>
              </div>
              {/* <button className="spb-announce-btn" onClick={() => { trackEventWithUTM('spb_hero_cta_click', 'seasonal_pooja_funnel', 'book_now_strip'); handleScrollToPlans(); }}>Book Now</button> */}
              <button className="spb-announce-btn" onClick={() => setShowMissedPopup(true)}>Book Now</button>
            </div>
          </div>

          {/* 1. HERO */}
          <section className="spb-hero">
            {/* Left: text content */}
            <div className="spb-hero-left">
              <p className="spb-hero-eyebrow">🪔 Chaitra Navratri 2026</p>
              <h1 className="spb-hero-headline">
                Maha Ashtami<br />
                <span>Navratri Puja</span>
              </h1>
              <p className="spb-hero-sub">
                Participate in sacred Saptashati paath on Maha Ashtami from anywhere in the world
                and receive blessed prashadam at home.
              </p>
              <ul className="spb-hero-trust">
                <li><span>🪔</span> Participation starts at <strong>₹1100 + GST</strong></li>
                <li><span>🛕</span> Sankalp Performed in Your Name</li>
                <li><span>📹</span> Get Sankalp Video of Your Puja</li>
              </ul>
              <div className="spb-hero-ctas">
                {/* <button className="spb-cta-primary" onClick={() => { trackEventWithUTM('spb_hero_cta_click', 'seasonal_pooja_funnel', 'book_now_hero'); handleScrollToPlans(); }}>
                  Book Your Maha Ashtami Puja Now
                </button> */}
                {/* <button className="spb-cta-secondary" onClick={() => { trackEventWithUTM('spb_hero_cta_click', 'seasonal_pooja_funnel', 'view_plans_hero'); handleScrollToPlans(); }}>
                  View Plans
                </button> */}
                <button className="spb-cta-primary" onClick={() => setShowMissedPopup(true)}>
                  Book Your Maha Ashtami Puja Now
                </button>
              </div>

              {/* Compact slot bar + countdown inside hero */}
              <div className="spb-hero-urgency">
                <div className="spb-hero-slots">
                  <p className="spb-hero-slots-label">⚡ Limited Sankalp Slots</p>
                  <div className="spb-hero-bar-wrap">
                    <div
                      className="spb-hero-bar-fill"
                      style={{ width: `${(BOOKED_SLOTS / TOTAL_SLOTS) * 100}%` }}
                    />
                  </div>
                  <p className="spb-hero-slots-text">
                    <strong>{slotsLeft} slots</strong> left.
                    {/* <strong>{slotsLeft} slots</strong> left, Book your slot //{TOTAL_SLOTS} */}
                    {/* <strong>Booking closing </strong> soon for Maha Ashtami  */}
                  </p>
                </div>
                <div className="spb-hero-countdown">
                  <p className="spb-hero-cd-label">Puja begins in</p>
                  <div className="spb-hero-cd-row">
                    <div className="spb-hero-cd-unit">
                      <span className="spb-hero-cd-num">{String(countdown.days).padStart(2, '0')}</span>
                      <span className="spb-hero-cd-lbl">Days</span>
                    </div>
                    <span className="spb-hero-cd-sep">:</span>
                    <div className="spb-hero-cd-unit">
                      <span className="spb-hero-cd-num">{String(countdown.hours).padStart(2, '0')}</span>
                      <span className="spb-hero-cd-lbl">Hrs</span>
                    </div>
                    <span className="spb-hero-cd-sep">:</span>
                    <div className="spb-hero-cd-unit">
                      <span className="spb-hero-cd-num">{String(countdown.minutes).padStart(2, '0')}</span>
                      <span className="spb-hero-cd-lbl">Min</span>
                    </div>
                    <span className="spb-hero-cd-sep">:</span>
                    <div className="spb-hero-cd-unit">
                      <span className="spb-hero-cd-num">{String(countdown.seconds).padStart(2, '0')}</span>
                      <span className="spb-hero-cd-lbl">Sec</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Right: image */}
            <div className="spb-hero-right">
              <OptimizedImage
                src={`${process.env.PUBLIC_URL}/images/services/durga.jpg`}
                alt="Navratri Puja"
                className="spb-hero-img"
              />
              <div className="spb-hero-img-badge">
                <span>&#128994;</span> Need help? Call: +91 9217588609 (9 AM – 7 PM IST)
              </div>
            </div>
          </section>

          {/* 2. TRUST STRIP */}
          <div className="spb-trust-strip">
            <div className="spb-trust-item">
              {/* <span className="spb-trust-icon">🪔</span> */}
              {/* <span className="spb-trust-num">2000+</span> */}
              <span className="spb-trust-lbl">Verified Vedic Acharyas</span>
            </div>
            <div className="spb-trust-divider" />
            <div className="spb-trust-item">
              {/* <span className="spb-trust-icon">⭐</span>
              <span className="spb-trust-num">4.9</span> */}
              <span className="spb-trust-lbl">Secure Payment</span>
            </div>
            <div className="spb-trust-divider" />
            <div className="spb-trust-item">
              {/* <span className="spb-trust-icon">🌍</span>
              <span className="spb-trust-num">25+</span> */}
              <span className="spb-trust-lbl">Quick Support</span>
            </div>
            <div className="spb-trust-divider" />
            <div className="spb-trust-item">
              {/* <span className="spb-trust-icon">📿</span>
              <span className="spb-trust-num">100%</span> */}
              <span className="spb-trust-lbl">Real Rituals</span>
            </div>
          </div>

          {/* 3. SIGNIFICANCE */}
          <section className="spb-significance">
            <div className="spb-sig-left">
              <h2 className="spb-sig-title">Why Maha Ashtami is <span>Powerful</span></h2>
              <p className="spb-sig-body">
                Maha Ashtami is the eighth and most sacred night of Navratri — the peak moment when
                Goddess Durga's divine energy is at its absolute highest. A Sankalp on this day carries
                the blessings of all nine nights, making it the most auspicious time to seek Devi's grace
                for yourself and your family.
              </p>
              <ul className="spb-sig-bullets">
                <li>Durga Ashtami holds the highest spiritual power of all nine nights</li>
                <li>Attracts health, peace and prosperity for your entire family</li>
                <li>Removes obstacles and clears the path for new beginnings</li>
              </ul>
            </div>
            <div className="spb-sig-right">
              <OptimizedImage
                src={`${process.env.PUBLIC_URL}/images/services/durga_yantra.jpg`}
                alt="Maha Ashtami Durga Puja"
                className="spb-sig-img"
              />
            </div>
          </section>

          {/* 3. HOW IT WORKS + WHAT YOU RECEIVE */}
          <section className="spb-how-receive">

            {/* LEFT: How It Works */}
            <div className="spb-how-col">
              <h2 className="spb-panel-title">How It Works</h2>
              <div className="spb-steps">
                <div className="spb-step">
                  <div className="spb-step-num">1</div>
                  <div className="spb-step-text">
                    <strong>Book Your Puja</strong>
                    <span>Choose your participation plan online</span>
                  </div>
                </div>
                <div className="spb-step-arrow">↓</div>
                <div className="spb-step">
                  <div className="spb-step-num">2</div>
                  <div className="spb-step-text">
                    <strong>Provide Sankalp Details</strong>
                    <span>Share your name, gotra and family members</span>
                  </div>
                </div>
                <div className="spb-step-arrow">↓</div>
                <div className="spb-step">
                  <div className="spb-step-num">3</div>
                  <div className="spb-step-text">
                    <strong>Ritual Performed</strong>
                    <span>Acharyas perform Saptashati paath in your name</span>
                  </div>
                </div>
                <div className="spb-step-arrow">↓</div>
                <div className="spb-step">
                  <div className="spb-step-num">4</div>
                  <div className="spb-step-text">
                    <strong>Provide Address Details</strong>
                    <span>Add your address after booking confirmation for prashadam delivery</span>
                  </div>
                </div>
                <div className="spb-step-arrow">↓</div>
                <div className="spb-step">
                  <div className="spb-step-num">5</div>
                  <div className="spb-step-text">
                    <strong>Receive Blessings</strong>
                    <span>Get puja video + prashadam delivered</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical divider */}
            <div className="spb-panel-divider" />

            {/* RIGHT: What You Will Receive */}
            <div className="spb-receive-col">
              <h2 className="spb-panel-title">What You Will Receive</h2>
              <ul className="spb-receive-list">
                <li><span className="spb-check">✔</span>Sankalp in your name &amp; family</li>
                <li><span className="spb-check">✔</span>Ritual performed by verified Acharyas</li>
                <li><span className="spb-check">✔</span>Sankalp video recording of your puja</li>
                <li><span className="spb-check">✔</span>Blessed prashadam delivered to your home</li>
                {/* <li><span className="spb-check">✔</span>Digital blessing certificate</li> */}
              </ul>
            </div>

          </section>

          {/* 4. PLANS */}
          <section className="spb-plans" ref={plansRef}>
            <h2 className="spb-plans-heading">Choose Your <span>Participation</span></h2>
            <p className="spb-plans-sub">Select the plan that fits you — from India or anywhere in the world</p>
            <div className="spb-plans-grid">
              <div className="spb-plan-card spb-plan-india">
                <div className="spb-plan-badge">🇮🇳 India</div>
                <div className="spb-plan-price">₹1,100 <span>+ GST</span></div>
                <ul>
                  <li>✅ Name &amp; Family Sankalp</li>
                  <li>✅ Sankalp Ritual Video</li>
                  <li>✅ Prashadam Delivered at your home</li>
                </ul>
                {/* <button className="spb-plan-btn" onClick={() => handleOpenForm('india')}>Book Now</button> */}
                <button className="spb-plan-btn" onClick={() => setShowMissedPopup(true)}>Book Now</button>
              </div>
              <div className="spb-plan-card spb-plan-intl">
                <div className="spb-plan-badge">🌍 International</div>
                <div className="spb-plan-price">₹3,500 <span>+ GST</span></div>
                <ul>
                  <li>✅ Name &amp; Family Sankalp</li>
                  <li>✅ Sankalp Ritual Video</li>
                  <li>✅ Global Prashadam Shipping</li>
                </ul>
                {/* <button className="spb-plan-btn" onClick={() => handleOpenForm('international')}>Book Now</button> */}
                <button className="spb-plan-btn" onClick={() => setShowMissedPopup(true)}>Book Now</button>
              </div>
            </div>
          </section>

          {/* 5. NRI BANNER */}
          <section className="spb-nri-banner">
            <div className="spb-nri-left">
              <p className="spb-nri-eyebrow">🌍 Join Navratri Puja From Anywhere</p>
              <p className="spb-nri-body">
                Devotees from <strong>USA, UK, UAE, Singapore, Canada</strong> and many other countries
                participate in sacred rituals performed by experienced Acharyas.
              </p>
              <p className="spb-nri-prashadam">📦 Prashadam shipped worldwide.</p>
              {/* <button className="spb-nri-btn" onClick={() => handleOpenForm('international')}>Book Your Puja</button> */}
              <button className="spb-nri-btn" onClick={() => setShowMissedPopup(true)}>Book Your Puja</button>
            </div>
            <div className="spb-nri-flags">
              <span title="USA">🇺🇸</span>
              <span title="UK">🇬🇧</span>
              <span title="UAE">🇦🇪</span>
              <span title="Singapore">🇸🇬</span>
              <span title="Canada">🇨🇦</span>
              <span title="Australia">🇦🇺</span>
            </div>
          </section>

          {/* YouTube Videos */}
          <YouTubeSection
            title="See Puja in Action"
            subtitle="Watch how we perform sacred rituals with devotion and Vedic precision"
            videos={seasonalPoojaVideos}
          />

          {/* 6. FAQ */}
          <section className="spb-faq">
            <h2 className="spb-faq-title">Frequently Asked Questions</h2>
            <div className="spb-faq-grid">
              <div className="spb-faq-item">
                <p className="spb-faq-q">Will my name be taken during puja?</p>
                <p className="spb-faq-a">→ Yes, the Acharya will chant your sankalp with your name and gotra.</p>
              </div>
              <div className="spb-faq-item">
                <p className="spb-faq-q">Can I include family members?</p>
                <p className="spb-faq-a">→ Yes, you can add up to 10 family member names in your sankalp.</p>
              </div>
              <div className="spb-faq-item">
                <p className="spb-faq-q">Will I receive a video recording?</p>
                <p className="spb-faq-a">→ Yes, sankalp recording of your puja will be shared with you after the ritual.</p>
              </div>
              <div className="spb-faq-item">
                <p className="spb-faq-q">When will prashadam arrive?</p>
                <p className="spb-faq-a">→ India: 7–10 days &nbsp;·&nbsp; International: 12–20 days</p>
              </div>
            </div>
          </section>

        </div>
      )}

      {/* ── MISSED POPUP ── */}
      {showMissedPopup && (
        <div className="spb-missed-overlay" onClick={() => setShowMissedPopup(false)}>
          <div className="spb-missed-modal" onClick={(e) => e.stopPropagation()}>
            <button className="spb-missed-close" onClick={() => setShowMissedPopup(false)} aria-label="Close">×</button>
            <div className="spb-missed-icon">🙏</div>
            <h2 className="spb-missed-title">Oh, you just missed it!</h2>
            <p className="spb-missed-body">
              All slots for <strong>Maha Ashtami Navratri Puja 2026</strong> have been filled.
              We are humbled by the overwhelming response from devotees across the world.
            </p>
            <p className="spb-missed-thanks">
              🌸 Thank you for your interest and devotion. May Maa Durga bless you and your family always.
            </p>
            <button
              className="spb-missed-explore-btn"
              onClick={() => {
                setShowMissedPopup(false);
                navigate('/', { state: { scrollToServices: true } });
              }}
            >
              Explore More Services from Antaryatra
            </button>
          </div>
        </div>
      )}
      {showForm && (
        <div className="spb-form-page" ref={formRef}>
          <div className="spb-form-topbar">
            <button className="spb-back-btn" onClick={handleBackFromForm}>
              &larr; Back
            </button>
            <h2>&#127655; Maha Ashtami Durga Puja</h2>
            <div className="spb-plan-toggle">
              <button
                className={`spb-plan-tab${selectedPlan === 'india' ? ' active' : ''}`}
                onClick={() => setSelectedPlan('india')}
              >
                🇮🇳 India
              </button>
              <button
                className={`spb-plan-tab${selectedPlan === 'international' ? ' active' : ''}`}
                onClick={() => setSelectedPlan('international')}
              >
                🌍 International
              </button>
            </div>
          </div>

          <div className="spb-form-body">
        <div className="booking-form">
          <div className="form-section">
            <h2>Primary Member Details</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  maxLength={50}
                  pattern="[a-zA-Z\s]*"
                  title="First name should contain only letters and spaces (max 50 characters)"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  maxLength={50}
                  pattern="[a-zA-Z\s]*"
                  title="Last name should contain only letters and spaces (max 50 characters)"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="gotra">Gotra (Optional)</label>
              <input
                id="gotra"
                type="text"
                value={gotra}
                onChange={(e) => setGotra(e.target.value)}
                placeholder="Enter gotra"
                maxLength={40}
                pattern="[a-zA-Z\s]*"
                title="Gotra should contain only letters and spaces (max 40 characters)"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="occasionName">
                  Occasion Name <span className="required">*</span>
                </label>
                <input
                  id="occasionName"
                  type="text"
                  value={occasionName}
                  onChange={(e) => setOccasionName(e.target.value)}
                  placeholder="e.g., Diwali Pooja"
                  required
                  disabled
                />
              </div>
              <div className="form-group">
                <label htmlFor="preferredDate">
                  Event Date <span className="required">*</span>
                </label>
                <input
                  id="preferredDate"
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  required
                  disabled
                />
              </div>
            </div>

            {/* <div className="form-group">
              <label htmlFor="specialRequirements">
                Special Requirements (Optional)
              </label>
              <textarea
                id="specialRequirements"
                value={specialRequirements}
                onChange={(e) => setSpecialRequirements(e.target.value)}
                placeholder="Any special requirements or notes"
                rows={3}
              />
            </div> */}
          </div>

          {/* Additional Family Members */}
          <div className="form-section">
            <div className="section-header">
              <h2>Additional Family Members</h2>
              <button
                type="button"
                onClick={handleAddFamilyMember}
                className="btn-add-member"
                disabled={familyMembers.length >= 10}
              >
                + Add Family Member
              </button>
            </div>
            
            {familyMembers.length > 0 && (
              <div className="family-members-list">
                {familyMembers.map((member, index) => (
                  <div key={index} className="family-member-card">
                    <div className="card-header">
                      <h3>Family Member {index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => handleRemoveFamilyMember(index)}
                        className="btn-remove"
                        aria-label="Remove family member"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`member-firstName-${index}`}>
                          First Name <span className="required">*</span>
                        </label>
                        <input
                          id={`member-firstName-${index}`}
                          type="text"
                          value={member.firstName}
                          onChange={(e) =>
                            handleFamilyMemberChange(index, 'firstName', e.target.value)
                          }
                          placeholder="Enter first name"
                          maxLength={50}
                          pattern="[a-zA-Z\s]*"
                          title="First name should contain only letters and spaces (max 50 characters)"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`member-lastName-${index}`}>
                          Last Name <span className="required">*</span>
                        </label>
                        <input
                          id={`member-lastName-${index}`}
                          type="text"
                          value={member.lastName}
                          onChange={(e) =>
                            handleFamilyMemberChange(index, 'lastName', e.target.value)
                          }
                          placeholder="Enter last name"
                          maxLength={50}
                          pattern="[a-zA-Z\s]*"
                          title="Last name should contain only letters and spaces (max 50 characters)"
                          required
                        />
                      </div>
                    </div>
                    {/* <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`member-relationship-${index}`}>
                          Relationship <span className="required">*</span>
                        </label>
                        <select
                          id={`member-relationship-${index}`}
                          value={member.relationship}
                          onChange={(e) =>
                            handleFamilyMemberChange(index, 'relationship', e.target.value)
                          }
                          required
                        >
                          <option value="">Select relationship</option>
                          <option value="Wife">Wife</option>
                          <option value="Husband">Husband</option>
                          <option value="Son">Son</option>
                          <option value="Daughter">Daughter</option>
                          <option value="Father">Father</option>
                          <option value="Mother">Mother</option>
                          <option value="Brother">Brother</option>
                          <option value="Sister">Sister</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor={`member-age-${index}`}>
                          Age <span className="required">*</span>
                        </label>
                        <input
                          id={`member-age-${index}`}
                          type="number"
                          min="1"
                          max="120"
                          value={member.age || ''}
                          onChange={(e) =>
                            handleFamilyMemberChange(index, 'age', e.target.value)
                          }
                          placeholder="Enter age"
                          required
                        />
                      </div>
                    </div> */}
                    <div className="form-group">
                      <label htmlFor={`member-gotra-${index}`}>Gotra (Optional)</label>
                      <input
                        id={`member-gotra-${index}`}
                        type="text"
                        value={member.gotra || ''}
                        onChange={(e) =>
                          handleFamilyMemberChange(index, 'gotra', e.target.value)
                        }
                        placeholder="Enter gotra"
                        maxLength={40}
                        pattern="[a-zA-Z\s]*"
                        title="Gotra should contain only letters and spaces (max 40 characters)"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {familyMembers.length === 0 && (
              <div className="no-members-message">
                <p>No additional family members added yet.</p>
                <p className="hint">
                  You can add up to 10 family members
                </p>
              </div>
            )}
          </div>

          {/* Price Summary */}
          <div className="price-summary">
            <h2>Price Summary</h2>
            {isValidPricingData(pricingData) ? (
              <div className="price-breakdown">
                <div className="price-item">
                  <span>Base Price (1 person)</span>
                  <span>₹{pricingData.basePrice.toLocaleString('en-IN')}</span>
                </div>
                {pricingData.calculatedMembersCount > 1 && (
                  <div className="price-item">
                    <span>Additional Members ({pricingData.calculatedMembersCount - 1})</span>
                    <span>
                      {isCalculatingPrice ? (
                        <span className="calculating">Calculating...</span>
                      ) : (
                        `₹${((pricingData.calculatedMembersCount - 1) * pricingData.perMemberPrice).toLocaleString('en-IN')}`
                      )}
                    </span>
                  </div>
                )}
                <div className="price-divider"></div>
                <div className="price-item">
                  <span>Subtotal</span>
                  <span>₹{pricingData.totalAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="price-item">
                  <span>GST ({Math.round(pricingData.gstRate * 100)}%)</span>
                  <span>₹{pricingData.gstAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="price-divider"></div>
                <div className="price-item total">
                  <span>Final Amount</span>
                  <span>₹{pricingData.finalAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="pricing-breakdown-text">
                  <p>{pricingData.pricingBreakdown}</p>
                </div>
              </div>
            ) : (
              <div className="price-breakdown">
                <div className="price-item">
                  <span>Base Price (1 person)</span>
                  <span>
                    {isCalculatingPrice ? (
                      <span className="calculating">Calculating...</span>
                    ) : (
                      'Enter details above'
                    )}
                  </span>
                </div>
                {familyMembers.length > 0 && (
                  <div className="price-item">
                    <span>Additional Members ({familyMembers.filter(m => m.firstName.trim() && m.lastName.trim()).length})</span>
                    <span>
                      {isCalculatingPrice ? (
                        <span className="calculating">Calculating...</span>
                      ) : (
                        'Enter details above'
                      )}
                    </span>
                  </div>
                )}
                <div className="price-divider"></div>
                <div className="price-item total">
                  <span>Total Amount</span>
                  <span>
                    {isCalculatingPrice ? (
                      <span className="calculating">Calculating...</span>
                    ) : (
                      'Will calculate automatically'
                    )}
                  </span>
                </div>
                {!firstName.trim() || !lastName.trim() ? (
                  <p className="pricing-note">Enter primary member details to get pricing</p>
                ) : familyMembers.some(m => (m.firstName.trim() || m.lastName.trim()) && (!m.firstName.trim() || !m.lastName.trim())) ? (
                  <p className="pricing-note">Complete family member details to get accurate pricing</p>
                ) : (
                  <p className="pricing-note">Pricing will calculate automatically</p>
                )}
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="form-actions">
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleProceedToPayment}
              className="btn-primary"
              disabled={isSubmitting || isCalculatingPrice}
            >
              {isSubmitting ? 'Registering Sankalp...' : 'Proceed to Payment'}
            </button>
          </div>
          
          {/* Secure Payment Info */}
         <div className="security-info" style={{ alignItems: 'center' , paddingTop: '20px'}}>
                <div className="security-item">
                  <span className="security-icon">🔒</span>
                  <span>SSL Encrypted payment with Razorpay</span>
                  <span className="security-icon">🛡️</span>
                  <span>100% Secure Transaction</span>
                </div>
              </div>
        </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonalPoojaBooking;
